import { ServiceError } from "@repo/core/globals";

type ErrorWithMetadata = {
  message?: string;
  code?: string;
  name?: string;
  status?: number;
  statusCode?: number;
  extensions?: {
    code?: string;
    statusCode?: number;
  };
  data?: {
    code?: string;
    status?: number;
    statusCode?: number;
    httpStatus?: number;
  };
  cause?: {
    status?: number;
    statusCode?: number;
  };
};

const AUTH_CODES = [
  "unauthorized",
  "forbidden",
  "unauthenticated",
  "permission-denied",
] as const;

function includesAuthKeyword(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const normalized = value.toLowerCase();
  return AUTH_CODES.some((code) => normalized.includes(code));
}

function isAuthStatus(value: unknown): boolean {
  return value === 401 || value === 403;
}

export default function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  if (error instanceof ServiceError) {
    return isAuthStatus(error.extensions?.statusCode);
  }

  const candidate = error as ErrorWithMetadata;

  if (
    isAuthStatus(candidate.status) ||
    isAuthStatus(candidate.statusCode) ||
    isAuthStatus(candidate.extensions?.statusCode) ||
    isAuthStatus(candidate.cause?.status) ||
    isAuthStatus(candidate.cause?.statusCode) ||
    isAuthStatus(candidate.data?.status) ||
    isAuthStatus(candidate.data?.statusCode) ||
    isAuthStatus(candidate.data?.httpStatus)
  ) {
    return true;
  }

  // Prefer structured codes over message substring matching.
  return (
    includesAuthKeyword(candidate.code) ||
    includesAuthKeyword(candidate.extensions?.code) ||
    includesAuthKeyword(candidate.data?.code) ||
    (typeof candidate.code === "string" && candidate.code.startsWith("auth/"))
  );
}
